import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import { useNavegacao } from '../../contexts/NavegacaoContext';
import { useKanban } from '../../contexts/KanbanContext';
import { useAtividade } from '../../contexts/AtividadeContext';
import { useAviso } from '../../contexts/AvisoContext';
import ListaAtividades from '../atividades/ListaAtividades';
import DepartamentosCards from '../departamento/DepartamentosCards';
import { format } from 'date-fns';
import { DashboardUsuarioData, DashboardUsuarioResponse } from '../../services/dashboardService';
import api from '../../services/api';
import pollingService from '../../services/PollingService';
import { Button, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SkeletonLoader from '../common/SkeletonLoader';

const Dashboard: React.FC = () => {
  const { usuario } = useAuth();
  const { ehLiderDepartamento } = useDepartamento();
  const { rotaAtual } = useNavegacao();
  const { recarregarTarefas } = useKanban();
  const { obterAvisos } = useAviso();
  const dataAtual = new Date();
  
  const [carregando, setCarregando] = useState<boolean>(true);
  const [dados, setDados] = useState<DashboardUsuarioData | null>(null);
  const [erro, setErro] = useState<string>('');
  const [atualizando, setAtualizando] = useState<boolean>(false);
  


  // Função para carregar dados do dashboard
  const carregarDados = useCallback(async (mostrarIndicadorAtualizacao = true) => {
    if (mostrarIndicadorAtualizacao) {
      setAtualizando(true);
    } else {
      setCarregando(true);
    }
    
    try {
      const response = await api.get('/estatisticas');
      const resultado = response.data as DashboardUsuarioResponse;
      
      if (resultado.success) {
        setDados({
          estatisticasUsuario: resultado.estatisticasUsuario,
          tarefasPorStatus: resultado.tarefasPorStatus,
          tarefasRecebidasPorStatus: resultado.tarefasRecebidasPorStatus,
          tarefasEnviadasPorStatus: resultado.tarefasEnviadasPorStatus,
          tarefasPorPrioridade: resultado.tarefasPorPrioridade,
          tarefasRecentesPorDia: resultado.tarefasRecentesPorDia
        });
        setErro('');
      } else {
        throw new Error('Resposta da API não contém dados válidos');
      }
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      setErro('Não foi possível carregar os dados do dashboard. Tente novamente mais tarde.');
    } finally {
      setCarregando(false);
      setAtualizando(false);
    }
  }, []);
  
  // Função para atualizar todos os dados do polling manualmente
  const atualizarTodosDados = useCallback(async () => {
    setAtualizando(true);
    
    try {
      console.log('Iniciando atualização manual de todos os dados...');
      
      // Atualizar dados do dashboard (estatísticas)
      await carregarDados(false);
      
      // Atualizar tarefas do Kanban
      if (recarregarTarefas) {
        console.log('Atualizando tarefas do Kanban...');
        await recarregarTarefas();
      }
      
      // Atualizar avisos
      if (obterAvisos) {
        console.log('Atualizando avisos...');
        await obterAvisos();
      }
      
      console.log('Atualização manual de todos os dados concluída!');
    } catch (error) {
      console.error('Erro ao atualizar todos os dados:', error);
    } finally {
      setAtualizando(false);
    }
  }, [carregarDados, recarregarTarefas, obterAvisos]);
  
  // Carregar dados inicialmente
  useEffect(() => {
    carregarDados(false);
  }, [carregarDados]);
  
  // Recarregar dados quando o usuário navegar para o dashboard
  useEffect(() => {
    if (rotaAtual === '/' && !carregando) {
      console.log('Dashboard detectou navegação para a rota principal, recarregando dados...');
      carregarDados();
    }
  }, [rotaAtual, carregarDados]);
  
  // Configurar polling para atualização automática das estatísticas
  useEffect(() => {
    // Iniciar polling apenas se o componente estiver montado e visível
    if (rotaAtual === '/') {
      console.log('Iniciando polling de estatísticas no Dashboard');
      
      // Função para processar estatísticas recebidas via polling
      const processarEstatisticasAtualizadas = (estatisticasAtualizadas: any[]) => {
        if (!estatisticasAtualizadas || estatisticasAtualizadas.length === 0) return;
        
        const resultado = estatisticasAtualizadas[0]; // Primeiro item do array
        
        if (resultado && resultado.success) {
          console.log('Atualizando estatísticas via polling');
          setDados({
            estatisticasUsuario: resultado.estatisticasUsuario,
            tarefasPorStatus: resultado.tarefasPorStatus,
            tarefasRecebidasPorStatus: resultado.tarefasRecebidasPorStatus,
            tarefasEnviadasPorStatus: resultado.tarefasEnviadasPorStatus,
            tarefasPorPrioridade: resultado.tarefasPorPrioridade,
            tarefasRecentesPorDia: resultado.tarefasRecentesPorDia
          });
        }
      };
      
      // Iniciar polling a cada 3 minutos para reduzir chamadas à API
      pollingService.iniciarPolling('estatisticas', processarEstatisticasAtualizadas, 180000);
      
      // Limpar polling ao desmontar o componente ou mudar de rota
      return () => {
        console.log('Parando polling de estatísticas no Dashboard');
        pollingService.pararPolling('estatisticas');
      };
    }
  }, [rotaAtual]);

  // Função para calcular a porcentagem de tarefas por status
  const calcularPorcentagem = (valor: number, total: number) => {
    return total > 0 ? Math.round((valor / total) * 100) : 0;
  };

  // Total de tarefas do usuário (soma de ativas e concluídas)
  const totalTarefas = dados ? 
    (dados.estatisticasUsuario.tarefasAtivas || 0) + 
    (dados.estatisticasUsuario.tarefasConcluidas || 0) : 0;

  // Dados para o gráfico de status
  const statusData = dados ? [
    { nome: 'Pendente', valor: dados.tarefasPorStatus.pendente, cor: '#3498db' },
    { nome: 'Em Andamento', valor: dados.tarefasPorStatus.emAndamento, cor: '#e67e22' },
    { nome: 'Aguardando Aprovação', valor: dados.tarefasPorStatus.aguardandoAprovacao, cor: '#f1c40f' },
    { nome: 'Ajustes Requisitados', valor: dados.tarefasPorStatus.ajustesRequisitados, cor: '#9b59b6' },
    { nome: 'Concluída', valor: dados.tarefasPorStatus.concluida, cor: '#2ecc71' }
  ] : [];
  
  // Dados para o gráfico de status por tipo de tarefa
  const statusRecebidasData = dados ? [
    { nome: 'Pendente', valor: dados.tarefasRecebidasPorStatus.pendente, cor: '#3498db' },
    { nome: 'Em Andamento', valor: dados.tarefasRecebidasPorStatus.emAndamento, cor: '#e67e22' },
    { nome: 'Aguardando Aprovação', valor: dados.tarefasRecebidasPorStatus.aguardandoAprovacao, cor: '#f1c40f' },
    { nome: 'Ajustes Requisitados', valor: dados.tarefasRecebidasPorStatus.ajustesRequisitados, cor: '#9b59b6' },
    { nome: 'Concluída', valor: dados.tarefasRecebidasPorStatus.concluida, cor: '#2ecc71' }
  ] : [];
  
  const statusEnviadasData = dados ? [
    { nome: 'Pendente', valor: dados.tarefasEnviadasPorStatus.aguardandoResposta, cor: '#3498db' },
    { nome: 'Em Execução', valor: dados.tarefasEnviadasPorStatus.emExecucao, cor: '#e67e22' },
    { nome: 'Aguardando Aprovação', valor: dados.tarefasEnviadasPorStatus.aguardandoAprovacao, cor: '#f1c40f' },
    { nome: 'Ajustes Requisitados', valor: dados.tarefasEnviadasPorStatus.ajustesRequisitados, cor: '#9b59b6' },
    { nome: 'Finalizada', valor: dados.tarefasEnviadasPorStatus.finalizada, cor: '#2ecc71' }
  ] : [];

  // Dados para o gráfico de prioridade
  const prioridadeData = dados ? [
    { nome: 'Baixa', valor: dados.tarefasPorPrioridade.baixa, cor: '#3498db' },
    { nome: 'Média', valor: dados.tarefasPorPrioridade.media, cor: '#f1c40f' },
    { nome: 'Alta', valor: dados.tarefasPorPrioridade.alta, cor: '#e67e22' },
    { nome: 'Urgente', valor: dados.tarefasPorPrioridade.urgente, cor: '#e74c3c' }
  ] : [];

  if (carregando) {
    return <SkeletonLoader />;
  }

  if (erro) {
    return (
      <div className="container mx-auto px-4">
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">Erro</h2>
          <p>{erro}</p>
          <button 
            className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            onClick={() => window.location.reload()}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-bravo-dark dark:text-white">Dashboard</h1>
        <Tooltip title="Atualizar todos os dados">
          <IconButton 
            onClick={atualizarTodosDados} 
            disabled={atualizando || carregando}
            color="primary"
          >
            <RefreshIcon className={atualizando ? 'animate-spin' : ''} />
          </IconButton>
        </Tooltip>
      </div>

      {/* Estatísticas do Usuário - PRIMEIRO ELEMENTO */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-gray-100 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Suas Tarefas
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center transition-transform hover:-translate-y-0.5 hover:shadow-md">
            <h3 className="flex items-center gap-2 text-gray-600 dark:text-gray-300 font-medium mb-2">
              <span className="h-2 w-2 rounded-full bg-blue-500" aria-hidden="true" />
              Tarefas Ativas
            </h3>
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{dados?.estatisticasUsuario.tarefasAtivas || 0}</p>
          </div>

          <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center transition-transform hover:-translate-y-0.5 hover:shadow-md">
            <h3 className="flex items-center gap-2 text-gray-600 dark:text-gray-300 font-medium mb-2">
              <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
              Tarefas Concluídas
            </h3>
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">{dados?.estatisticasUsuario.tarefasConcluidas || 0}</p>
          </div>

          <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center transition-transform hover:-translate-y-0.5 hover:shadow-md">
            <h3 className="flex items-center gap-2 text-gray-600 dark:text-gray-300 font-medium mb-2">
              <span className="h-2 w-2 rounded-full bg-purple-500" aria-hidden="true" />
              Total de Tarefas
            </h3>
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{totalTarefas}</p>
          </div>
        </div>

        {/* Estatísticas Detalhadas */}
        <div className="mt-6 border-t dark:border-gray-700 pt-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-700 dark:text-gray-200">Estatísticas Detalhadas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
              <ul className="space-y-2">
                <li className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Tarefas como responsável:</span>
                  <span className="font-medium dark:text-gray-100">
                    {dados && dados.tarefasRecebidasPorStatus ?
                      (dados.tarefasRecebidasPorStatus.pendente || 0) +
                      (dados.tarefasRecebidasPorStatus.emAndamento || 0) +
                      (dados.tarefasRecebidasPorStatus.aguardandoAprovacao || 0) +
                      (dados.tarefasRecebidasPorStatus.ajustesRequisitados || 0) +
                      (dados.tarefasRecebidasPorStatus.concluida || 0) : 0}
                  </span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Tarefas como criador:</span>
                  <span className="font-medium dark:text-gray-100">
                    {dados && dados.tarefasEnviadasPorStatus ?
                      (dados.tarefasEnviadasPorStatus.aguardandoResposta || 0) +
                      (dados.tarefasEnviadasPorStatus.emExecucao || 0) +
                      (dados.tarefasEnviadasPorStatus.aguardandoAprovacao || 0) +
                      (dados.tarefasEnviadasPorStatus.ajustesRequisitados || 0) +
                      (dados.tarefasEnviadasPorStatus.finalizada || 0) : 0}
                  </span>
                </li>
                <li className="flex justify-between font-semibold">
                  <span className="text-gray-700 dark:text-gray-200">Total (responsável OU criador):</span>
                  <span className="dark:text-gray-100">{totalTarefas}</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
              <ul className="space-y-2">
                <li className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Tarefas concluídas:</span>
                  <span className="font-medium dark:text-gray-100">{dados?.estatisticasUsuario.tarefasConcluidas || 0}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Tarefas ativas (não concluídas):</span>
                  <span className="font-medium dark:text-gray-100">{dados?.estatisticasUsuario.tarefasAtivas || 0}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Tarefas pendentes:</span>
                  <span className="font-medium dark:text-gray-100">{dados && dados.tarefasPorStatus ? dados.tarefasPorStatus.pendente || 0 : 0}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Tarefas em andamento:</span>
                  <span className="font-medium dark:text-gray-100">{dados && dados.tarefasPorStatus ? dados.tarefasPorStatus.emAndamento || 0 : 0}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Lista de Atividades */}
      <ListaAtividades />


      {/* Cards de Departamentos - Novo componente */}
      <DepartamentosCards />
      
    </div>
  );
};

export default Dashboard;
